import { EDeviceType } from '@libs/types';

/**
 * UA Client Hints `formFactors` values (Chromium 132+) mapped onto our device taxonomy.
 * Keys are lower-cased because the spec's casing has changed between iterations ('Desktop' vs 'desktop').
 */
const FORM_FACTOR_MAP: Record<string, EDeviceType> = {
    desktop: EDeviceType.PC,
    mobile: EDeviceType.Mobile,
    tablet: EDeviceType.Tablet,
    tv: EDeviceType.TV,
    watch: EDeviceType.Watch,
    xr: EDeviceType.Other,
    eink: EDeviceType.Other,
    automotive: EDeviceType.Other,
};

// Checked in this order: a smart watch UA also contains 'Android', a Fire TV UA also contains 'Silk'.
const WATCH_RE = /watch|wear\s?os/i;
const TV_RE = /smart-?tv|googletv|appletv|crkey|hbbtv|netcast|nettv|viera|web[o0]s|tizen|roku|bravia/i;
/** Fire TV model codes (AFTB, AFTKA, AFTKMST12, …). Case-sensitive on purpose: they are always upper-case, unlike words such as 'after'. */
const FIRE_TV_RE = /\bAFT[A-Z0-9]{1,8}\b/;
const CONSOLE_RE = /playstation|xbox|nintendo/i;
const TABLET_RE = /ipad|tablet|playbook|silk|kindle|\bsm-t/i;
const MOBILE_RE = /iphone|ipod|android.*mobile|windows\s?phone|blackberry|bb10|opera mini|iemobile|mobile safari/i;
// Checked after the mobile patterns: an Android UA also contains 'Linux', an iPadOS UA also contains 'Macintosh'.
const DESKTOP_OS_RE = /windows nt|macintosh|mac os x|cros|x11|linux/i;

/** Smallest CSS-pixel screen side from which a touch device is treated as a tablet rather than a phone. */
const TABLET_MIN_SIDE_PX = 600;

/**
 * iPadOS 13+ Safari reports a desktop ('Macintosh') user agent and exposes no form factors,
 * so a multi-touch macOS device is the only way to tell an iPad from a Mac.
 */
const isIPadOs = () => {
    const isMac =
        navigator?.platform === 'MacIntel' || navigator?.userAgentData?.platform === 'macOS' || /macintosh|mac os x/i.test(navigator?.userAgent ?? '');
    return isMac && (navigator?.maxTouchPoints ?? 0) > 1;
};

/**
 * A touch-only device: coarse pointer and no hover. Deliberately stricter than `maxTouchPoints > 0`,
 * which is also true for a desktop with a touchscreen next to its mouse.
 */
const isTouchPrimary = () => window?.matchMedia?.('(pointer: coarse) and (hover: none)')?.matches ?? false;

/** Tablets and phones are indistinguishable by UA in some WebViews, so fall back to the physical screen size. */
const isTabletSizedScreen = () => Math.min(window?.screen?.width ?? 0, window?.screen?.height ?? 0) >= TABLET_MIN_SIDE_PX;

/**
 * Resolves the device form factor for `IEnvironmentData.deviceType`.
 *
 * Signals, most to least reliable: UA Client Hints `formFactors` (exact, Chromium only) →
 * user agent patterns → the iPadOS desktop-UA special case → touch capability plus screen size.
 *
 * @param formFactors `formFactors` from `getHighEntropyValues`, when the browser exposes them.
 */
export const getDeviceType = (formFactors?: readonly string[]): EDeviceType => {
    const fromHints = formFactors?.map((factor) => FORM_FACTOR_MAP[factor?.toLowerCase()]).find(Boolean);
    if (fromHints) {
        return fromHints;
    }

    const userAgent = navigator?.userAgent ?? '';
    if (WATCH_RE.test(userAgent)) return EDeviceType.Watch;
    if (TV_RE.test(userAgent) || FIRE_TV_RE.test(userAgent)) return EDeviceType.TV;
    if (CONSOLE_RE.test(userAgent)) return EDeviceType.Other;
    if (TABLET_RE.test(userAgent)) return EDeviceType.Tablet;
    if (MOBILE_RE.test(userAgent)) return EDeviceType.Mobile;
    if (isIPadOs()) return EDeviceType.Tablet;
    // Android without the 'Mobile' token is a tablet by Chrome's own convention.
    if (/android/i.test(userAgent)) return EDeviceType.Tablet;
    if (DESKTOP_OS_RE.test(userAgent)) return EDeviceType.PC;
    // Unknown UA (custom WebView, minor browser): fall back to input capabilities.
    if (isTouchPrimary()) return isTabletSizedScreen() ? EDeviceType.Tablet : EDeviceType.Mobile;

    return EDeviceType.PC;
};
